import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, BookmarkPlus, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export function SaveSearchButton({ filters }) {
  const { user, setAuthOpen, saveSearch } = useAuth();
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!user) {
      setAuthOpen(true);
      toast.info("Sign in to save this search and get email alerts.");
      return;
    }
    const parts = [filters.category !== "All" && filters.category, filters.budgetLabel !== "Any Budget" && filters.budgetLabel, filters.status !== "Any Status" && filters.status, filters.listing !== "New + Resale" && filters.listing, filters.query && `"${filters.query}"`].filter(Boolean);
    const name = parts.length ? parts.join(" · ") : "All GYC listings";
    setBusy(true);
    try {
      await saveSearch({ name, alerts: true, filters: { category: filters.category, budget_min: filters.budgetMin, budget_max: filters.budgetMax, status: filters.status, listing: filters.listing, query: filters.query } });
      toast.success(`Saved: ${name} - we'll email you when a matching listing appears.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save this search.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button type="button" data-testid="save-search-btn" onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] border border-[#D4AF37]/50 text-[#F3E5AB] hover:bg-[#D4AF37]/10 transition-colors rounded-[2px] disabled:opacity-50">
      <BookmarkPlus size={14} /> {busy ? "Saving..." : "Save search & alert me"}
    </button>
  );
}

export function SavedSearchesPanel() {
  const { user, savedSearches, loadSavedSearches, toggleSearchAlerts, deleteSearch, toggleDigest } = useAuth();
  useEffect(() => { loadSavedSearches(); }, [loadSavedSearches]);
  const digestOn = user?.digest_opt_in ?? true;

  return (
    <section className="mt-16" data-testid="saved-searches-panel">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Saved Searches &amp; Alerts</p>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="font-serif-lux text-3xl text-[#F5F5F0] mb-2">Your Watchlist.</h2>
          <p className="text-sm text-[#71717A]">We email you the moment a new listing matches a search with alerts on.</p>
        </div>
        <label className="flex items-center gap-3 border border-[#D4AF37]/20 rounded-[2px] px-4 py-3 cursor-pointer" data-testid="digest-toggle-label">
          <input type="checkbox" data-testid="digest-toggle" checked={digestOn} onChange={toggleDigest} className="accent-[#D4AF37] w-4 h-4" />
          <span className="text-[12px] text-[#A1A1AA]"><span className="text-[#F5F5F0]">Friday digest</span> &middot; weekly summary of new matches &amp; corridor news</span>
        </label>
      </div>
      {savedSearches.length === 0 ? (
        <p className="border border-[#D4AF37]/15 rounded-[2px] p-6 text-sm text-[#71717A]" data-testid="saved-searches-empty">No saved searches yet. Set filters in the Inventory section and tap &ldquo;Save search &amp; alert me&rdquo;.</p>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-4" data-testid="saved-searches-list">
          {savedSearches.map((s) => (
            <li key={s.id} className="card-lux p-5 rounded-[2px] flex items-start justify-between gap-4" data-testid={`saved-search-${s.id}`}>
              <div>
                <p className="font-serif-lux text-lg text-[#F5F5F0]">{s.name}</p>
                <p className="text-[11px] text-[#71717A] mt-1">{s.alerts ? "Email alerts on" : "Alerts paused"} &middot; {s.matches_sent} match{s.matches_sent === 1 ? "" : "es"} sent</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`saved-search-toggle-${s.id}`} onClick={() => toggleSearchAlerts(s)} aria-pressed={s.alerts} aria-label="Toggle alerts" className={`p-2 border rounded-[2px] transition-colors ${s.alerts ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#D4AF37]/25 text-[#71717A]"}`}>
                  {s.alerts ? <Bell size={14} /> : <BellOff size={14} />}
                </button>
                <button data-testid={`saved-search-delete-${s.id}`} onClick={() => deleteSearch(s)} aria-label="Delete saved search" className="p-2 border border-[#D4AF37]/25 text-[#71717A] hover:text-[#F3E5AB] rounded-[2px] transition-colors"><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
